import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apartmentService } from '../services/apartmentService';
import {
  CreateApartmentRequest,
  UpdateApartmentRequest,
  UpdateApartmentAddressRequest,
  UpdateApartmentDetailsRequest,
  UpdateApartmentPricingRequest,
  UpdateFeaturesRequest,
  PhotoDto,
} from '../types/apartment';

const QUERY_KEY = 'apartments';

export const useApartments = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: apartmentService.getAll,
  });
};

export const useApartment = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => apartmentService.getById(id),
    enabled: !!id,
  });
};

export const useCreateApartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateApartmentRequest) => apartmentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateApartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApartmentRequest }) =>
      apartmentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

// Individual update hooks
export const useUpdateApartmentAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApartmentAddressRequest }) =>
      apartmentService.updateAddress(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateApartmentDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApartmentDetailsRequest }) =>
      apartmentService.updateDetails(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateApartmentPricing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApartmentPricingRequest }) =>
      apartmentService.updatePricing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteApartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const usePublishApartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUnpublishApartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.unpublish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdatePhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, photos }: { id: string; photos: PhotoDto[] }) =>
      apartmentService.updatePhotos(id, { photos }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useRankApartmentUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.rankUp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useRankApartmentDown = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.rankDown(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useRankApartmentToTop = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apartmentService.rankToTop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateFeatures = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeaturesRequest }) =>
      apartmentService.updateFeatures(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
